import { useMemo, useState } from 'react';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import { createInstantlyCampaign } from '@/services/research';

export function EmailBuilder() {
  const userEmail = useAppStore((s) => s.userEmail);
  const [campaignName, setCampaignName] = useState('');
  const [subjectA, setSubjectA] = useState('');
  const [bodyA, setBodyA] = useState('');
  const [subjectB, setSubjectB] = useState('');
  const [bodyB, setBodyB] = useState('');
  const [followUpDelay, setFollowUpDelay] = useState('3');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const hasFollowUp = useMemo(() => {
    return Boolean(subjectB.trim() && bodyB.trim());
  }, [bodyB, subjectB]);

  const canCreate = useMemo(() => {
    return Boolean(campaignName.trim() && subjectA.trim() && bodyA.trim());
  }, [bodyA, campaignName, subjectA]);

  const handleCreate = async () => {
    if (!userEmail) return;
    setSaving(true);
    setStatus(null);
    try {
      const steps = [
        { subject: subjectA, body: bodyA, delayDays: 0 },
        ...(hasFollowUp
          ? [
              {
                subject: subjectB,
                body: bodyB,
                delayDays: Math.max(1, Number(followUpDelay) || 1),
              },
            ]
          : []),
      ];
      const result = await createInstantlyCampaign({
        userEmail,
        name: campaignName,
        steps,
        stopOnReply: true,
        openTracking: true,
        linkTracking: true,
      });
      if (!result.success || !result.campaign?.id) {
        throw new Error(result.error || 'Instantly email publish failed.');
      }
      setStatus(`Email sequence published to campaign ${result.campaign.name || campaignName} (${result.campaign.id}).`);
    } catch (error: any) {
      setStatus(error?.message || 'Failed to publish email sequence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Builder</h1>
          <p className="text-gray-400 mt-2">Draft sequence copy and publish directly to Instantly as a campaign.</p>
        </div>

        {status && (
          <div className="rounded border border-gray-700 bg-gray-900/60 px-4 py-3 text-sm text-gray-200">
            {status}
          </div>
        )}

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Campaign</h2>
          <input
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="Campaign Name"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
        </section>

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Email 1 (Required)</h2>
          <input
            value={subjectA}
            onChange={(e) => setSubjectA(e.target.value)}
            placeholder="Subject"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
          <textarea
            value={bodyA}
            onChange={(e) => setBodyA(e.target.value)}
            rows={10}
            placeholder="Email body"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
        </section>

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Email 2 (Optional Follow-up)</h2>
          <input
            value={subjectB}
            onChange={(e) => setSubjectB(e.target.value)}
            placeholder="Follow-up Subject"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
          <textarea
            value={bodyB}
            onChange={(e) => setBodyB(e.target.value)}
            rows={8}
            placeholder="Follow-up Body"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
          <input
            value={followUpDelay}
            onChange={(e) => setFollowUpDelay(e.target.value)}
            placeholder="Follow-up Delay Days"
            className="w-56 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
          />
        </section>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCreate}
            disabled={saving || !canCreate}
            className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
          >
            {saving ? 'Publishing…' : 'Publish To Instantly'}
          </button>
        </div>
      </main>
    </div>
  );
}
